import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType } from '@capacitor/camera';
import Header from '../components/Header';
import StatsCard from '../components/StatsCard';
import ProgressBar from '../components/ProgressBar';
import ScanButton from '../components/ScanButton';
import BadgeGrid from '../components/BadgeGrid';
import UpgradeBanner from '../components/UpgradeBanner';
import { useAuth } from '../hooks/useAuth';
import { analyzeImage } from '../lib/api';
import type { Badge } from '../types';

export default function HomePage() {
    const { user, userData, isPro, scansRemaining } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const totalScans = userData?.scansUsedThisMonth ?? 0;
    const streak = 5; // TODO: Calculate from scan history

    const badges: Badge[] = [
        { id: 'first_scan', name: 'First Scan', icon: '♻️', earned: totalScans > 0 },
        { id: 'streak_7', name: '7-Day Streak', icon: '🔥', earned: streak >= 7 },
        { id: 'scans_100', name: '100 Scans', icon: '⭐', earned: totalScans >= 100 },
        { id: 'planet_saver', name: 'Planet Saver', icon: '🌍', earned: totalScans >= 500 },
    ];

    async function handleScan() {
        if (!user) {
            navigate('/auth');
            return;
        }

        if (!isPro && scansRemaining <= 0) {
            navigate('/upgrade');
            return;
        }

        setError(null);
        setLoading(true);

        try {
            let imageBase64: string;

            // Use Capacitor Camera on iOS, file input on web
            if (Capacitor.isNativePlatform()) {
                const photo = await Camera.getPhoto({
                    quality: 80,
                    allowEditing: false,
                    resultType: CameraResultType.Base64
                });
                imageBase64 = photo.base64String!;
            } else {
                // Web fallback: trigger file input
                fileInputRef.current?.click();
                return; // The file input onChange will handle the rest
            }

            const result = await analyzeImage(imageBase64);
            navigate('/result', { state: { result } });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Scan failed');
        } finally {
            setLoading(false);
        }
    }

    async function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        if (!file) return;

        setError(null);
        setLoading(true);

        try {
            // Convert file to base64
            const reader = new FileReader();
            reader.onload = async () => {
                const base64 = (reader.result as string).split(',')[1];
                try {
                    const result = await analyzeImage(base64);
                    navigate('/result', { state: { result } });
                } catch (err) {
                    setError(err instanceof Error ? err.message : 'Analysis failed');
                } finally {
                    setLoading(false);
                }
            };
            reader.readAsDataURL(file);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to read file');
            setLoading(false);
        }

        // Reset input
        event.target.value = '';
    }

    return (
        <div className="min-h-screen bg-primary-50">
            <Header />

            <main className="max-w-lg mx-auto px-4 py-6 safe-bottom">
                {/* Stats Row */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <StatsCard icon="♻️" value={totalScans} label="Total Scans" />
                    <StatsCard icon="🔥" value={streak} label="Day Streak" highlight />
                </div>

                {/* Progress Bar */}
                <ProgressBar
                    current={userData?.scansUsedThisMonth ?? 0}
                    max={10}
                    isPro={isPro}
                />

                {/* Upgrade Banner */}
                {!isPro && <UpgradeBanner compact />}

                {/* Scan Button */}
                <div className="text-center my-8">
                    <ScanButton onClick={handleScan} loading={loading} disabled={!isPro && scansRemaining <= 0} />
                    <p className="text-gray-500 text-sm mt-2">Tap to scan any item</p>

                    {error && (
                        <p className="text-red-500 text-sm mt-4">{error}</p>
                    )}
                </div>

                {/* Hidden file input for web */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleFileSelect}
                />

                {/* Badges */}
                <BadgeGrid badges={badges} />
            </main>
        </div>
    );
}
